/**
 * MediaEmbed Component - YouTube/Vimeo video embedding
 *
 * Features:
 * - Loading state while iframe loads
 * - Error handling for invalid URLs
 * - Thumbnail preview before play
 * - Responsive 16:9 aspect ratio
 *
 * Supports:
 * - YouTube watch URLs (youtube.com/watch?v=...)
 * - YouTube short URLs (youtu.be/...)
 * - YouTube embed URLs (youtube.com/embed/...)
 * - Vimeo URLs (vimeo.com/... or player.vimeo.com/video/...)
 */

import React, { useState } from 'react';
import './MediaEmbed.css';

function MediaEmbed({ url, thumbnail }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [thumbIndex, setThumbIndex] = useState(0);

  if (!url) return null;

  /**
   * Parse video URL and extract video ID and platform.
   */
  const parseVideoUrl = (inputUrl) => {
    // YouTube
    const youtubeMatch = inputUrl.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
    );
    if (youtubeMatch) {
      return {
        platform: 'youtube',
        id: youtubeMatch[1],
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`,
        thumbnailUrl: `https://img.youtube.com/vi/${youtubeMatch[1]}/hqdefault.jpg`
      };
    }

    // Vimeo
    const vimeoMatch = inputUrl.match(
      /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/
    );
    if (vimeoMatch) {
      return {
        platform: 'vimeo',
        id: vimeoMatch[1],
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`,
        thumbnailUrl: null // Vimeo requires API for thumbnails
      };
    }

    return null;
  };

  const videoInfo = parseVideoUrl(url);

  if (!videoInfo) {
    return (
      <div className="media-embed media-embed--error">
        <div className="media-embed__error-content">
          <span className="media-embed__error-icon">!</span>
          <p>Invalid video URL</p>
        </div>
      </div>
    );
  }

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  const handlePlayClick = () => {
    setShowVideo(true);
  };

  // Thumbnail first. The iframe (and its autoplay) mounts only after Play.
  const thumbnails = [thumbnail, videoInfo.thumbnailUrl].filter(Boolean);
  const thumbnailSrc = thumbnails[thumbIndex];
  if (!showVideo) {
    return (
      <div className="media-embed media-embed--thumbnail">
        {thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt="Video thumbnail"
            className="media-embed__thumbnail"
            onError={() => setThumbIndex((index) => index + 1)}
          />
        ) : (
          <div className="media-embed__poster" role="img" aria-label="Video thumbnail" />
        )}
        <button
          type="button"
          className="media-embed__play-button"
          onClick={handlePlayClick}
          aria-label="Play video"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="media-embed media-embed--error">
        <div className="media-embed__error-content">
          <span className="media-embed__error-icon">!</span>
          <p>Failed to load video</p>
        </div>
      </div>
    );
  }

  return (
    <div className="media-embed">
      {loading && (
        <div className="media-embed__loading">
          <div className="media-embed__spinner" />
        </div>
      )}
      <iframe
        src={videoInfo.embedUrl}
        title="Video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        style={{ opacity: loading ? 0 : 1 }}
      />
    </div>
  );
}

export default MediaEmbed;
